import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Download, Clock, Users, Soup, Search, Filter, BookOpen } from "lucide-react";
import logoLight from "@/assets/brand/local-hustle-recipes-light.png";
import logoDark from "@/assets/brand/local-hustle-recipes-dark.png";
import PlatformHeader from "@/components/PlatformHeader";

const COURSE_LABEL = {
  starter: "Starter", soup: "Soup", tapas: "Tapas",
  entree: "Entrée", main: "Main", dessert: "Dessert",
};

/**
 * Phase 20-21 — Local Hustle Recipes (chapter-based recipe book).
 *
 * Customer surface for the cross-tenant recipe library. Hero is branded
 * with the official Local Hustle Recipes logo and links to the full
 * chapter-structured PDF (cuisine intro → course sections → recipes with
 * wine pairings). Below the hero, a "Browse by chapter" grid lists every
 * cuisine chapter with its own per-cuisine downloadable mini-book.
 */
export default function RecipeBookPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, cuisines: [], courses: [] });
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = params.get("q") || "";
  const cuisine = params.get("cuisine") || "";
  const course = params.get("course") || "";

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (cuisine) qs.set("cuisine", cuisine);
    if (course) qs.set("course", course);
    qs.set("per_page", "100");
    Promise.all([
      api.get(`/hustle/recipes?${qs.toString()}`),
      api.get("/recipes-chapters"),
    ])
      .then(([rRes, cRes]) => {
        setData(rRes.data);
        setChapters(cRes.data || []);
      })
      .finally(() => setLoading(false));
  }, [q, cuisine, course]);

  const filtered = data.items || [];

  const setFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const pdfHref = useMemo(() => {
    const qs = new URLSearchParams();
    if (cuisine) qs.set("cuisine", cuisine);
    if (course) qs.set("course", course);
    return `${apiBase}/api/recipes-book.pdf?${qs.toString()}`;
  }, [cuisine, course, apiBase]);

  return (
    <div data-testid="recipe-book-page" className="bg-[#FFFAF0] dark:bg-stone-950 min-h-screen">
      {/* ONE header for every module — see /app/memory/AGENT_RULES.md Rule #4 */}
      <PlatformHeader
        brand={{ name: "Local Hustle Recipes", to: "/recipes-book" }}
        nav={[
          { to: "/recipes-book",    label: "Cookbook" },
          { to: "/recipes",         label: "Recipes" },
          { to: "/recipe-planner",  label: "Meal Planner" },
          { to: "/my-cookbook",     label: "My Cookbook" },
        ]}
        ctaLabel="Download PDF"
        ctaLink={pdfHref}
        showBookCta={true}
        showCart={false}
      />

      {/* COVER */}
      <header className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-[#F6EFE0] via-[#FFFAF0] to-[#FFE7D3]"
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18] mix-blend-multiply"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1495546968767-f0573cca821e?auto=format&fit=crop&w=2400&q=70")', backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 md:pt-16 md:pb-32 grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <p className="font-medium text-xs uppercase tracking-[0.32em] text-[#C05746]">
              The cookbook
            </p>
            <div className="mt-8 w-16 h-[1.5px] bg-[#A88842]" />
            <h1 className="mt-6 font-serif-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-[#1A1410] max-w-4xl">
              Recipes worth&nbsp;cooking.
            </h1>
            <p className="mt-6 font-serif-display italic text-xl sm:text-2xl text-[#6B6157] max-w-3xl">
              {data.total} dishes pulled from real menus across our agency network,
              organised into cuisine chapters with course breakdowns and wine pairings.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://hustleplate.au/recipes"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="explore-portal-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#C05746] text-white px-5 py-3 text-sm font-bold hover:bg-[#A64A3C] shadow-lg"
              >
                Explore 2,400+ recipes →
              </a>
              <a
                href="#chapters"
                data-testid="browse-chapters-cta"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A1410]/20 bg-white/70 backdrop-blur px-5 py-3 text-sm font-medium hover:bg-white"
              >
                <BookOpen className="h-4 w-4" />
                Browse by cuisine
              </a>
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-pdf-cta"
                title="Download printed book (PDF)"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur border border-[#1A1410]/15 px-3 py-2 text-xs font-medium text-[#1A1410]/70 hover:bg-white hover:text-[#1A1410]"
              >
                <Download className="h-3.5 w-3.5" />
                PDF
              </a>
            </div>
          </div>
          <img
            src={logoLight}
            alt="Local Hustle Recipes"
            data-testid="recipe-book-logo"
            className="hidden lg:block w-72 h-auto drop-shadow-2xl"
          />
        </div>
      </header>

      {/* CHAPTERS — one card per cuisine with per-cuisine PDF download */}
      <section id="chapters" className="bg-[#1A1410] text-[#F6EFE0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <p className="font-medium text-xs uppercase tracking-[0.32em] text-[#C9A24A]">
                The chapters
              </p>
              <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl">
                {chapters.length} cuisines, one cookbook
              </h2>
              <p className="mt-3 text-[#F6EFE0]/70 max-w-2xl">
                Each chapter opens with a page about the cuisine, then breaks
                the recipes into starter / main / dessert with wine pairings.
                Download the whole book or any individual cuisine.
              </p>
            </div>
            <img src={logoDark} alt="" aria-hidden className="hidden md:block w-28 h-auto opacity-90" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {chapters.map((ch) => (
              <ChapterCard key={ch.cuisine} ch={ch} apiBase={apiBase} />
            ))}
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <div className="sticky top-0 z-30 bg-[#FFFAF0]/95 backdrop-blur border-y border-black/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          <label className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6157]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Search recipes, ingredients…"
              data-testid="recipe-search-input"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-black/12 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C05746]/40"
            />
          </label>
          <select
            value={cuisine}
            onChange={(e) => setFilter("cuisine", e.target.value)}
            data-testid="recipe-cuisine-filter"
            className="rounded-full border border-black/12 bg-white text-sm px-3 py-2"
          >
            <option value="">All cuisines</option>
            {(data.cuisines || []).map((c) => (
              <option key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>
            ))}
          </select>
          <select
            value={course}
            onChange={(e) => setFilter("course", e.target.value)}
            data-testid="recipe-course-filter"
            className="rounded-full border border-black/12 bg-white text-sm px-3 py-2"
          >
            <option value="">All courses</option>
            {(data.courses || []).map((c) => (
              <option key={c} value={c}>{COURSE_LABEL[c] || c}</option>
            ))}
          </select>
          {(q || cuisine || course) && (
            <button
              type="button"
              onClick={() => setParams({}, { replace: true })}
              data-testid="recipe-clear-filters"
              className="text-sm text-[#C05746] hover:underline"
            >
              Clear
            </button>
          )}
          <span className="ml-auto text-xs text-[#6B6157] hidden sm:block">
            <Filter className="inline h-3.5 w-3.5 mr-1" />
            {filtered.length} of {data.total}
          </span>
        </div>
      </div>

      {/* GRID */}
      <main id="recipes-grid" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 scroll-mt-20">
        {loading ? (
          <p className="text-[#6B6157] text-sm">Loading the cookbook…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Soup className="h-10 w-10 text-[#6B6157] mx-auto" />
            <p className="mt-4 font-serif-display text-2xl">No recipes match those filters.</p>
            <button onClick={() => setParams({}, { replace: true })} className="mt-2 text-[#C05746] underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((r, idx) => (
              <RecipeCard key={r.slug} recipe={r} index={idx} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


function ChapterCard({ ch, apiBase }) {
  const pdf = `${apiBase}/api/recipes-book.pdf?cuisine=${encodeURIComponent(ch.cuisine)}`;
  return (
    <div
      data-testid={`chapter-card-${ch.cuisine}`}
      className="relative rounded-2xl overflow-hidden border border-white/10 group"
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
        style={{ backgroundImage: `url("${ch.hero_image}")`, filter: "brightness(0.45) saturate(1.1)" }}
      />
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(180deg, transparent 40%, ${ch.accent}aa 100%)` }}
      />
      <div className="relative p-5 min-h-[260px] flex flex-col justify-end text-white">
        <p className="text-xs uppercase tracking-[0.22em] font-medium" style={{ color: ch.accent }}>
          Chapter · {ch.count} recipe{ch.count !== 1 ? "s" : ""}
        </p>
        <h3 className="mt-2 font-serif-display text-3xl">{ch.label}</h3>
        <p className="italic text-sm text-white/80 mt-1 line-clamp-2">{ch.tagline}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/recipes?cuisine=${encodeURIComponent(ch.cuisine)}#recipes-grid`}
            data-testid={`chapter-browse-${ch.cuisine}`}
            onClick={() => {
              // Smooth-scroll to the grid in case we're already on /recipes
              // and only the query string changes (React Router won't trigger
              // a fresh mount, so the hash alone won't move the viewport).
              setTimeout(() => {
                const el = document.getElementById("recipes-grid");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 80);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium rounded-full bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5"
          >
            Browse →
          </Link>
          <a
            href={pdf}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`chapter-download-${ch.cuisine}`}
            className="inline-flex items-center gap-1 text-xs font-medium rounded-full bg-white text-[#1A1410] hover:bg-white/90 px-3 py-1.5"
          >
            <Download className="h-3 w-3" />
            Download chapter PDF
          </a>
        </div>
      </div>
    </div>
  );
}


function RecipeCard({ recipe, index }) {
  const time = (recipe.prep_minutes || 0) + (recipe.cook_minutes || 0);
  return (
    <Link
      to={`/recipes/${recipe.slug}`}
      data-testid={`recipe-card-${recipe.slug}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-black/8 shadow-sm hover:shadow-xl transition-shadow"
    >
      <div
        className="aspect-[4/3] bg-[#F6EFE0] bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
        style={recipe.hero_image_url ? { backgroundImage: `url("${recipe.hero_image_url}")` } : {}}
      />
      <div className="p-5">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#C05746] font-medium">
          <span>№ {String(index + 1).padStart(2, "0")} · {(recipe.cuisine || "").replace(/_/g, " ")}</span>
          <span>{COURSE_LABEL[recipe.course] || recipe.course}</span>
        </div>
        <h3 className="mt-2 font-serif-display text-2xl text-[#1A1410] group-hover:text-[#C05746] transition-colors">
          {recipe.title}
        </h3>
        {recipe.subtitle && (
          <p className="mt-1 italic text-[#6B6157] text-sm line-clamp-1">{recipe.subtitle}</p>
        )}
        <div className="mt-4 flex items-center gap-4 text-xs text-[#6B6157]">
          {recipe.serves && (
            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {recipe.serves}</span>
          )}
          {time > 0 && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {time} min</span>
          )}
          {(recipe.dietary || []).slice(0, 2).map((d) => (
            <span key={d} className="inline-flex rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] uppercase tracking-wide">
              {d.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

